import { z } from 'zod'
import type { StatusTone } from '@/design-system'
import { isValidPhone, PHONE_INVALID_MESSAGE } from '@/shared/domain/formatPhone'
import { formatPhone } from './smsSettings'
import { formatSmsDateTime, scopeLabel } from './smsJournal'

/**
 * Consentements SMS par numéro (`sms_consent_list_v1`, `sms_consent_detail_v1`). Deux autorités distinctes :
 * l'autorisation RECA (preuve par portée) et le blocage du fournisseur après STOP, que seul le client lève par
 * START. Un numéro au dossier, un contrat ou un import ne valent jamais inscription.
 */

export const consentScopes = ['SNOW_ALERTS', 'ADMINISTRATIVE'] as const
export type ConsentScope = (typeof consentScopes)[number]

export const consentStates = ['GRANTED', 'PENDING', 'REVOKED', 'REFUSED'] as const
export type ConsentState = (typeof consentStates)[number]

export const consentRowSchema = z.object({
  contactId: z.string(),
  phone: z.string(),
  clients: z.array(z.object({ clientId: z.string(), status: z.string(), name: z.string().nullable() })),
  snowAlerts: z.enum(consentStates).nullable(),
  administrative: z.enum(consentStates).nullable(),
  openRequest: z.object({ scope: z.string(), expiresAt: z.string() }).nullable(),
  localBlockedAt: z.string().nullable(),
  localBlockReason: z.string().nullable(),
  providerOptedOutAt: z.string().nullable(),
  providerOptOutType: z.string().nullable(),
  lastEvent: z
    .object({ kind: z.string(), scope: z.string().nullable(), source: z.string(), at: z.string() })
    .nullable(),
  updatedAt: z.string(),
})
export type ConsentRow = z.infer<typeof consentRowSchema>

export const consentListSchema = z.object({ rows: z.array(consentRowSchema), total: z.number().int() })
export type ConsentList = z.infer<typeof consentListSchema>

export const consentDetailSchema = consentRowSchema.extend({
  proofs: z.array(
    z.object({
      scope: z.string(),
      source: z.string(),
      wordingText: z.string().nullable(),
      wordingVersion: z.string().nullable(),
      proofNote: z.string().nullable(),
      actorName: z.string().nullable(),
      at: z.string(),
    }),
  ),
  events: z.array(
    z.object({
      id: z.string(),
      kind: z.string(),
      scope: z.string().nullable(),
      source: z.string(),
      wordingVersion: z.string().nullable(),
      proofNote: z.string().nullable(),
      actorName: z.string().nullable(),
      at: z.string(),
    }),
  ),
})
export type ConsentDetail = z.infer<typeof consentDetailSchema>

export type ConsentStateFilter = ConsentState | 'NONE' | 'BLOCKED' | ''

export interface ConsentFilter {
  search?: string
  scope?: ConsentScope | ''
  state?: ConsentStateFilter
}

/**
 * Texte officiel de consentement, par portée (rédigé le 16 octobre 2026 à la demande du propriétaire). Une version
 * publiée ne change jamais : tout changement de texte reçoit une nouvelle version, les preuves gardent la leur.
 */
export const officialConsentWordings: Record<ConsentScope, { version: string; text: string }> = {
  SNOW_ALERTS: {
    version: 'alertes-2026-10-16',
    text: 'J’accepte de recevoir de Groupe RECA, par SMS au numéro indiqué, des alertes liées au déneigement de ma propriété : opération prévue, passage, véhicule à déplacer, retard ou annulation. La fréquence varie selon la météo. Des frais de messagerie texte et de données peuvent s’appliquer selon mon forfait. Je peux cesser de recevoir tous les SMS de Groupe RECA en tout temps en répondant STOP ou ARRÊT. Mon consentement n’est pas une condition de mon contrat.',
  },
  ADMINISTRATIVE: {
    version: 'administratif-2026-10-16',
    text: 'J’accepte de recevoir de Groupe RECA, par SMS au numéro indiqué, des avis administratifs liés à mon contrat : contrat à signer, facture, reçu de paiement et rappel d’échéance. Des frais de messagerie texte et de données peuvent s’appliquer selon mon forfait. Je peux cesser de recevoir tous les SMS de Groupe RECA en tout temps en répondant STOP ou ARRÊT. Mon consentement n’est pas une condition de mon contrat.',
  },
}

/** Blocages qu'un agent peut lever (décision du 16 oct. 2026) ; STOP et Telnyx : seul le client, par START. */
export function canLiftBlock(row: ConsentRow): boolean {
  return (
    row.providerOptedOutAt === null &&
    (row.localBlockReason === 'AGENT_REQUEST' || row.localBlockReason === 'REFUSED_SIGNUP')
  )
}

export interface ConsentLiftCommand {
  contactId: string
  note: string
}

export interface ConsentProofCommand {
  phone: string
  scope: ConsentScope
  wordingText: string
  wordingVersion: string
  proofNote: string
  /** Date d'acceptation (AAAA-MM-JJ) ; vide : maintenant. */
  occurredOn: string
}

export interface ConsentWithdrawalCommand {
  contactId: string
  scopes: ConsentScope[]
  blockAll: boolean
  note: string
}

const stateDisplay: Record<ConsentState, { label: string; tone: StatusTone }> = {
  GRANTED: { label: 'Inscrit', tone: 'success' },
  PENDING: { label: 'À confirmer', tone: 'pending' },
  REVOKED: { label: 'Retiré', tone: 'neutral' },
  REFUSED: { label: 'Refusé', tone: 'neutral' },
}

export function consentStateDisplay(state: ConsentState | null): { label: string; tone: StatusTone } {
  return state ? stateDisplay[state] : { label: 'Aucune', tone: 'neutralLight' }
}

export const consentStateOptions: { value: ConsentStateFilter; label: string }[] = [
  { value: 'GRANTED', label: 'Inscrit' },
  { value: 'PENDING', label: 'À confirmer' },
  { value: 'REVOKED', label: 'Retiré' },
  { value: 'REFUSED', label: 'Refusé' },
  { value: 'NONE', label: 'Aucune preuve' },
  { value: 'BLOCKED', label: 'Bloqué' },
]

export const consentScopeOptions = consentScopes.map((scope) => ({ value: scope, label: scopeLabel(scope) }))

const localBlockLabels: Record<string, string> = {
  STOP: 'STOP reçu',
  REFUSED_SIGNUP: 'Inscription refusée',
  AGENT_REQUEST: 'Retrait par un agent',
  PROVIDER_ERROR_40300: 'Refus Telnyx (STOP)',
  PROVIDER_ERROR_21610: 'Refus fournisseur (STOP)',
}

/** Blocage en vigueur : le fournisseur prime, puis le blocage local. */
export function blockDisplay(row: ConsentRow): { label: string; tone: StatusTone } {
  if (row.providerOptedOutAt) return { label: 'Bloqué par Telnyx', tone: 'neutralStrong' }
  if (row.localBlockedAt) {
    return {
      label: localBlockLabels[row.localBlockReason ?? ''] ?? 'Bloqué',
      tone: 'neutralStrong',
    }
  }
  return { label: 'Aucun', tone: 'neutralLight' }
}

export function isBlocked(row: ConsentRow): boolean {
  return row.providerOptedOutAt !== null || row.localBlockedAt !== null
}

/** Nom affiché : dossier(s) rattaché(s), sinon « Numéro sans dossier ». */
export function consentContactName(row: ConsentRow): string {
  const names = row.clients.map((client) => client.name ?? 'Client sans nom')
  if (names.length === 0) return 'Numéro sans dossier'
  return names.join(' / ')
}

export function consentStateOf(row: ConsentRow, scope: ConsentScope): ConsentState | null {
  return scope === 'SNOW_ALERTS' ? row.snowAlerts : row.administrative
}

/** Ce qui peut partir vers ce numéro aujourd'hui, et ce qui ne le peut pas, avec la raison. */
export function consentAuthorization(row: ConsentRow): { allowed: string[]; blocked: string[] } {
  if (isBlocked(row)) {
    return { allowed: [], blocked: [`Tous les SMS (${blockDisplay(row).label.toLowerCase()})`] }
  }
  const allowed: string[] = []
  const blocked: string[] = []
  for (const scope of consentScopes) {
    const state = consentStateOf(row, scope)
    if (state === 'GRANTED') allowed.push(scopeLabel(scope))
    else blocked.push(`${scopeLabel(scope)} (${consentStateDisplay(state).label.toLowerCase()})`)
  }
  allowed.push('Réponse à un message récent du client')
  return { allowed, blocked }
}

/** Avertissements du détail, dans l'ordre de gravité. */
export function consentAlerts(row: ConsentRow): string[] {
  const alerts: string[] = []
  if (row.providerOptedOutAt) {
    alerts.push(
      'Le client a répondu STOP : Telnyx bloque ce numéro. Seul le client peut lever ce blocage en répondant START. Une preuve enregistrée ici ne le lève pas.',
    )
  } else if (row.localBlockedAt) {
    alerts.push(
      row.localBlockReason === 'STOP'
        ? 'STOP reçu : aucun SMS ne part. Le blocage se lève seulement si le client répond START.'
        : `Numéro bloqué (${blockDisplay(row).label.toLowerCase()}) : aucun SMS ne part, même avec une preuve. Le blocage se lève seulement sur nouvelle demande du client, avec une note.`,
    )
  }
  if (row.openRequest) {
    alerts.push(
      `Demande d’inscription envoyée (${scopeLabel(row.openRequest.scope).toLowerCase()}) : en attente d’un OUI jusqu’au ${formatSmsDateTime(row.openRequest.expiresAt)}.`,
    )
  }
  if (row.clients.length > 1) {
    alerts.push(
      `Numéro présent sur ${String(row.clients.length)} dossiers : aucune information financière ou contractuelle n’est envoyée tant que l’association n’est pas résolue.`,
    )
  } else if (row.clients.some((client) => client.status !== 'CONFIRMED')) {
    alerts.push(
      'Association du numéro au dossier à confirmer : aucun document financier ou contractuel n’est envoyé.',
    )
  }
  return alerts
}

const eventKindLabels: Record<string, string> = {
  REQUESTED: 'Demande d’inscription envoyée',
  GRANTED: 'Inscription',
  REFUSED: 'Inscription refusée',
  REVOKED: 'Retrait',
  STOP: 'STOP reçu',
  START: 'START reçu',
  HELP: 'HELP reçu',
  GLOBAL_BLOCK: 'Blocage de tous les SMS',
  GLOBAL_UNBLOCK: 'Blocage levé',
  PROVIDER_OPT_OUT: 'Blocage Telnyx posé',
  PROVIDER_OPT_IN: 'Blocage Telnyx levé',
}

const sourceLabels: Record<string, string> = {
  WEB_FORM: 'formulaire en ligne',
  CONTRACT_FLOW: 'parcours de contrat',
  PUBLIC_PAGE: 'page publique',
  OFFLINE_PROOF: 'preuve hors ligne',
  SMS_KEYWORD: 'mot-clé SMS',
  SMS_REPLY: 'réponse SMS',
  AGENT: 'agent',
  PROVIDER: 'Telnyx',
}

export function consentSourceLabel(source: string): string {
  return sourceLabels[source] ?? source
}

/** « Inscription · Alertes opérationnelles · preuve hors ligne · F. Bastien ». */
export function consentEventLabel(event: ConsentDetail['events'][number]): string {
  return [
    eventKindLabels[event.kind] ?? event.kind,
    event.scope ? scopeLabel(event.scope) : null,
    consentSourceLabel(event.source),
    event.actorName,
  ]
    .filter(Boolean)
    .join(' · ')
}

/** Résumé du dernier événement pour la liste. */
export function lastEventLabel(row: ConsentRow): string {
  if (!row.lastEvent) return 'Aucun événement'
  const kind = eventKindLabels[row.lastEvent.kind] ?? row.lastEvent.kind
  return `${kind} · ${formatSmsDateTime(row.lastEvent.at)}`
}

export function consentPhoneLabel(row: ConsentRow): string {
  return formatPhone(row.phone)
}

/** Erreurs de saisie d'une preuve, avant tout appel serveur (le serveur revérifie). */
export function proofFormErrors(
  command: ConsentProofCommand,
  today: string,
): Partial<Record<keyof ConsentProofCommand, string>> {
  const errors: Partial<Record<keyof ConsentProofCommand, string>> = {}
  if (!isValidPhone(command.phone)) errors.phone = PHONE_INVALID_MESSAGE
  if (!command.wordingText.trim()) errors.wordingText = 'Recopiez le texte exact accepté par le client.'
  if (!command.wordingVersion.trim()) errors.wordingVersion = 'Version du texte obligatoire.'
  if (!command.proofNote.trim()) errors.proofNote = 'Décrivez où la preuve est conservée.'
  if (command.occurredOn && command.occurredOn > today) errors.occurredOn = 'Date future impossible.'
  return errors
}

/** Date du jour à Montréal (AAAA-MM-JJ). */
export function torontoToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Toronto' }).format(now)
}

/** Date d'acceptation saisie (AAAA-MM-JJ) : midi à Montréal ; aujourd'hui ou vide, l'heure du serveur. */
export function proofOccurredAt(occurredOn: string, today: string): string | null {
  if (!occurredOn || occurredOn >= today) return null
  return `${occurredOn}T12:00:00-05:00`
}
